import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function ServicesSection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  const services = [
    {
      step: t("services.step1"),
      title: t("services.step1Title"),
      price: t("services.step1Price"),
      description: t("services.step1Desc"),
      includes: [
        t("services.step1Inc1"),
        t("services.step1Inc2"),
        t("services.step1Inc3"),
        t("services.step1Inc4"),
        t("services.step1Inc5"),
      ],
      image: {
        src: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20quiet%20Japanese%20residential%20neighborhood%20with%20traditional%20and%20modern%20houses%20overcast%20sky%20calm%20atmosphere%20muted%20warm%20earth%20tones%20documentary%20photography%20no%20faces%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-svc-01&orientation=landscape",
        alt: "Aerial view of a quiet Japanese residential neighborhood, representing property sourcing",
      },
    },
    {
      step: t("services.step2"),
      title: t("services.step2Title"),
      price: t("services.step2Price"),
      description: t("services.step2Desc"),
      includes: [
        t("services.step2Inc1"),
        t("services.step2Inc2"),
        t("services.step2Inc3"),
        t("services.step2Inc4"),
        t("services.step2Inc5"),
      ],
      image: {
        src: "https://readdy.ai/api/search-image?query=Close-up%20of%20Japanese%20carpenter%20hands%20planing%20wood%20with%20traditional%20hand%20tools%20in%20a%20warm%20workshop%20with%20wood%20shavings%20soft%20overhead%20light%20muted%20earth%20tones%20documentary%20style%20no%20faces%20visible%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-svc-02&orientation=landscape",
        alt: "Japanese carpenter hand-planing wood with traditional tools, representing renovation craftsmanship",
      },
    },
    {
      step: t("services.step3"),
      title: t("services.step3Title"),
      price: t("services.step3Price"),
      description: t("services.step3Desc"),
      includes: [
        t("services.step3Inc1"),
        t("services.step3Inc2"),
        t("services.step3Inc3"),
        t("services.step3Inc4"),
        t("services.step3Inc5"),
      ],
      image: {
        src: "https://readdy.ai/api/search-image?query=Styled%20Japanese%20tatami%20guest%20room%20with%20shoji%20screens%20futon%20bedding%20and%20warm%20lamp%20light%20serene%20peaceful%20atmosphere%20no%20people%20muted%20warm%20earth%20tones%20editorial%20interior%20photography%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-svc-03&orientation=landscape",
        alt: "Styled Japanese tatami guest room with shoji screens, representing rental launch",
      },
    },
    {
      step: t("services.step4"),
      title: t("services.step4Title"),
      price: t("services.step4Price"),
      description: t("services.step4Desc"),
      includes: [
        t("services.step4Inc1"),
        t("services.step4Inc2"),
        t("services.step4Inc3"),
        t("services.step4Inc4"),
        t("services.step4Inc5"),
        t("services.step4Inc6"),
      ],
      image: {
        src: "https://readdy.ai/api/search-image?query=Neatly%20made%20Japanese%20guesthouse%20interior%20with%20folded%20white%20linens%20on%20a%20futon%20morning%20light%20streaming%20through%20a%20window%20hospitable%20warm%20atmosphere%20no%20people%20muted%20earth%20tones%20editorial%20photography%20no%20on-image%20text%20no%20logos&width=600&height=400&seq=noren-svc-04&orientation=landscape",
        alt: "Neatly made Japanese guesthouse interior with morning light, representing full management",
      },
    },
  ];

  return (
    <section id="services" className="w-full bg-paper py-20 md:py-28 scroll-mt-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mb-14 md:mb-20">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-4">
            {t("services.sectionLabel")}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-ink leading-[1.1] mb-5">
            {t("services.title")}
            <br />
            <span className="italic font-normal text-ink/70">{t("services.titleItalic")}</span>
            <br />
            {t("services.titleLine3")}
          </h2>
          <p className="text-base text-ink/50 leading-relaxed max-w-xl font-light">
            {t("services.intro")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {services.map((service, i) => (
            <div
              key={service.step}
              className="bg-white border border-hairline hover:border-ink/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer overflow-hidden"
              onClick={() => setActiveIndex(activeIndex === i ? null : i)}
            >
              <div className="relative h-44 md:h-48 overflow-hidden">
                <img
                  src={service.image.src}
                  alt={service.image.alt}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-90 hover:scale-[1.03] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent"></div>
              </div>

              <div className="p-6 md:p-8 pt-4 md:pt-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <span className="font-serif text-4xl md:text-5xl text-gold/30 font-semibold">
                    {service.step}
                  </span>
                  <span className="text-xs font-medium text-ink/70 bg-paper-secondary px-3 py-1.5 border border-hairline whitespace-nowrap">
                    {service.price}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-ink mb-2">
                  {service.title}
                </h3>
                <p className="text-sm text-ink/50 leading-relaxed mb-4 font-light">
                  {service.description}
                </p>
                <div className="flex items-center gap-1 text-gold text-sm font-medium">
                  <span>{activeIndex === i ? t("services.showLess") : t("services.whatsIncluded")}</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-300 ${activeIndex === i ? "rotate-180" : ""}`}></i>
                </div>
              </div>

              {activeIndex === i && (
                <div className="px-6 md:px-8 pb-6 md:pb-8 pt-0 border-t border-hairline">
                  <ul className="mt-5 space-y-2.5">
                    {service.includes.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-ink/50 font-light">
                        <i className="ri-check-line text-gold mt-0.5"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}